/** @jsxImportSource @emotion/react */
import { UserCircleIcon } from "@heroicons/react/outline";
import { Accordion, useAccordionItemContext } from "@reach/accordion";
import "@reach/accordion/styles.css";
import { forwardRef, Fragment } from "react";
import { Link } from "react-router-dom";
import "twin.macro";
import FileSelector from "../post-analysis/components/files/FileSelector";
import { Badge, BadgeDot } from "../shared/Badge";
import { PrimaryLinkButton } from "../shared/Button";
import { formatDate } from "../shared/formatDate";
import {
  AdjustmentsOutlineIcon,
  ArrowsExpandOutlineIcon,
  BookmarkOutlineIcon,
  CalendarOutlineIcon,
  LocationMarkerOutlineIcon,
  LockClosedIcon,
  SettingsOutlineIcon,
} from "../shared/Icons";
import {
  ListItem,
  ListItemContent,
  ListItemEmptyMetaTag,
  ListItemMeta,
  ListItemMetaTag,
  ListItemRow,
} from "../shared/List";
import { BadgeSkeleton, Skeleton } from "../shared/Skeleton";
import { upperFirst } from "../shared/upperFirst";
import { PositionOnVehicleShortLabel } from "./PositionOnVehicle";
import {
  TireTestCalulTest,
  TireTestDefects,
  TireTestDeltaPressure,
  TireTestDXPName,
  TireTestFirstRunReference,
  TireTestFxMax,
  TireTestFxMean,
  TireTestFyMax,
  TireTestFyMean,
  TireTestFzMax,
  TireTestFzMean,
  TireTestNothingToDeclare,
  TireTestPeakZ,
  TireTestQuantity,
  TireTestSerialNumber,
  TireTestVMax,
  TireTestVMean,
  TireTestWeightAfterTest,
  TireTestWeightBeforeTest,
} from "./TireTestDescriptionTerms";
import { PRIORITY_LABELS } from "./TireTestQueries";
import { CamberFormatter, NumberFormatter, PressureFormatter } from "./UnitFormatters";
import { useTireTestName } from "./useTireTestName";

/** @typedef {{ id: import("./TireTestQueries").TireTestStatus, name: string, color: string }} Status */
/** @type { [index: string]: Status} */
export const statuses = {
  archived: { id: "archived", name: "Archived", color: "gray" },
  draft: { id: "draft", name: "Draft", color: "gray" },
  ready: { id: "ready", name: "Requested", color: "yellow" },
  planned: { id: "planned", name: "Planned", color: "yellow" },
  tested: { id: "tested", name: "Tested", color: "green" },
  analysed: { id: "analysed", name: "Analysed", color: "green" },
};

export const StatusBadge = ({ status }) => {
  const foundStatus = statuses[status];
  if (!foundStatus) {
    return null;
  }
  return (
    <Badge color={foundStatus.color}>
      <BadgeDot />
      {foundStatus.name}
    </Badge>
  );
};

export const RegderBadge = ({ isREGDER, regdersCount }) => {
  if (!isREGDER) {
    return null;
  }
  return (
    <Badge color="indigo">
      REGDER {`${regdersCount !== undefined ? `(${regdersCount})` : ""}`}
    </Badge>
  );
};

export const PriorityBadge = ({ priority }) => {
  if (priority === undefined || priority === 3) return null;

  const color = priority === 0 ? "red" : priority === 1 ? "pink" : "blue";
  return <Badge color={color}>{PRIORITY_LABELS[priority]}</Badge>;
};

/** @type {(tireTest: import("./TireTestQueries").TireTest) => string} */
const TireTestDateLabel = ({ tireTest }) => {
  switch (tireTest.status) {
    case "tested":
    case "analysed":
      return tireTest.testedDate
        ? `Tested on ${formatDate(tireTest.testedDate)}`
        : "Not tested yet";
    case "planned":
      return tireTest.meta?.session
        ? `Planned on ${formatDate(tireTest.meta.session.from)}`
        : "Not planned yet";
    default:
      return tireTest.dueDate ? `Due on ${formatDate(tireTest.dueDate)}` : "Not due yet";
  }
};

/** @type {(stages: import("./TireTestQueries").TireTest) => number} */
const getTotalCompletion = (tireTest) => {
  if (!tireTest || !tireTest.stages) {
    // If test is not executed, stages might not be filled yet.
    return 0;
  }
  // Sum all stage completions, e.g.:
  // - stage 1: 100%
  // - stage 2: 100%
  // - stage 3: 42%
  // - stage 4: undefined
  // total completion will be 242
  const totalCompletion = tireTest.stages
    .map((stage) => stage?.completion)
    .filter((completion) => completion !== undefined && !Number.isNaN(completion))
    .reduce((totalCompletion, completion) => totalCompletion + completion, 0);
  // Divide by 100 to display as stage completion (e.g.: 2.42 / 4)
  return totalCompletion / 100;
};

/** @type {(stages: import("./TireTestQueries").TireTest) => number} */
const getRequestedStageCount = (tireTest) => {
  if (!tireTest || !tireTest.requestedStageCount) {
    return 0;
  }
  return tireTest.requestedStageCount;
};

/** @type {(tireTest: import("./TireTestQueries").TireTest) => boolean} */
const isTireTestInError = (tireTest) => {
  return (
    tireTest.stages?.find((stage) => stage.status === "failed") ||
    tireTest.defects?.find((defect) => defect.checked === true) ||
    tireTest.regders[tireTest.regders.length - 1]?.status === "failed"
  );
};

export const TireTestListItemSkeleton = ({ as: Component = ListItem }) => {
  return (
    <Component>
      <ListItemContent>
        <ListItemRow>
          <Skeleton />
          <BadgeSkeleton />
        </ListItemRow>
        <ListItemRow>
          <ListItemMeta>
            <ListItemMetaTag>
              <BookmarkOutlineIcon />
              <Skeleton />
            </ListItemMetaTag>
            <ListItemMetaTag>
              <LocationMarkerOutlineIcon />
              <Skeleton />
            </ListItemMetaTag>
            <ListItemMetaTag>
              <AdjustmentsOutlineIcon />
              <Skeleton />
            </ListItemMetaTag>
          </ListItemMeta>

          <ListItemMetaTag as="p">
            <CalendarOutlineIcon />
            <Skeleton />
          </ListItemMetaTag>
        </ListItemRow>
      </ListItemContent>
    </Component>
  );
};

export const TireTestMetaTags = ({ tireTest, MetaTag, EmptyMetaTag }) => {
  const isForceAndMoment = tireTest.meta?.session?.isForceAndMoment ?? false;
  return (
    <>
      <MetaTag>
        <BookmarkOutlineIcon width={24} height={24} />
        <span tw="truncate">{tireTest.category || <EmptyMetaTag />}</span>
      </MetaTag>
      <MetaTag>
        <LocationMarkerOutlineIcon width={24} height={24} />
        <span tw="truncate">{tireTest.circuit || <EmptyMetaTag />}</span>
      </MetaTag>
      <MetaTag>
        <AdjustmentsOutlineIcon width={24} height={24} />
        <span tw="truncate">
          <PressureFormatter>{tireTest.pressureInBars}</PressureFormatter>
          {!isForceAndMoment && (
            <>
              {" ꞏ "}
              <CamberFormatter>{tireTest.staticCamberInDegrees}</CamberFormatter>
            </>
          )}
        </span>
      </MetaTag>
      <MetaTag>
        <ArrowsExpandOutlineIcon width={24} height={24} />
        <span tw="truncate">
          {PositionOnVehicleShortLabel[tireTest.positionOnVehicle] || <EmptyMetaTag />}
        </span>
      </MetaTag>

      <MetaTag>
        <UserCircleIcon tw="h-4 w-auto" />
        <span tw="truncate">{tireTest.developer?.name || <EmptyMetaTag />}</span>
      </MetaTag>

      <MetaTag>
        <SettingsOutlineIcon width={24} height={24} />
        <span tw="truncate">
          {(tireTest.machinePosition && upperFirst(tireTest.machinePosition)) || <EmptyMetaTag />}
        </span>
      </MetaTag>
    </>
  );
};

/** @type {React.FC<{ tireTest: import("./TireTestQueries").TireTest }>} */
const TireTestStatusBadge = ({ tireTest }) => {
  const status = tireTest.status;
  const statusName = statuses[status] ? statuses[status].name : status;
  const statusColor = statuses[status] ? statuses[status].color : "gray";
  const totalCompletion = getTotalCompletion(tireTest);
  const requestedStageCount = getRequestedStageCount(tireTest);
  switch (tireTest.status) {
    case "tested":
    case "analysed":
      return (
        <Badge
          color={isTireTestInError(tireTest) ? "red" : "green"}
          title={`This test has status "${statusName}" with ${totalCompletion} stages completed out of ${requestedStageCount} requested.`}
        >
          <BadgeDot />
          <span>
            {statusName} (
            <NumberFormatter maximumFractionDigits={2}>{totalCompletion}</NumberFormatter>/
            {requestedStageCount})
          </span>
        </Badge>
      );
    case "ready":
    case "planned":
      return (
        <Badge
          color={statusColor}
          title={`This test has status "${statusName}" with ${totalCompletion} stages requested.`}
        >
          <BadgeDot />
          <span>
            {statusName} ({requestedStageCount})
          </span>
        </Badge>
      );
    default:
      return (
        <Badge color={statusColor} title={`This test has status "${statusName}".`}>
          <BadgeDot />
          {statusName}
        </Badge>
      );
  }
};

/** @type {React.FC<{ tireTest: import("./TireTestQueries").TireTest }>} */
export const TireTestListItemSummary = ({ tireTest }) => {
  const tireTestName = useTireTestName(tireTest);
  return (
    <>
      <ListItemRow>
        <div tw="flex items-center space-x-2 truncate">
          <PrimaryLinkButton as={Link} to={`/tire-tests/${tireTest._id}`} tw="truncate">
            {tireTestName}
          </PrimaryLinkButton>
          <RegderBadge isREGDER={tireTest.isREGDER} regdersCount={tireTest.regders?.length} />
          <PriorityBadge priority={tireTest.priorityIndicator} />
          {tireTest.isLocked && (
            <LockClosedIcon tw="-mr-1 ml-2 h-3 w-3 text-gray-500 flex-shrink-0" />
          )}
        </div>
        <TireTestStatusBadge tireTest={tireTest} />
      </ListItemRow>
      <ListItemRow>
        <ListItemMeta>
          <TireTestMetaTags
            tireTest={tireTest}
            MetaTag={ListItemMetaTag}
            EmptyMetaTag={ListItemEmptyMetaTag}
          />
        </ListItemMeta>

        <ListItemMetaTag as="p">
          <CalendarOutlineIcon width={24} height={24} />
          <span>
            <TireTestDateLabel tireTest={tireTest} />
          </span>
        </ListItemMetaTag>
      </ListItemRow>
    </>
  );
};

/** @type {React.FC<{ tireTest: import("./TireTestQueries").TireTest }>} */
export const TireTestPostAnalysisListItemSummary = ({ tireTest }) => {
  const tireTestName = useTireTestName(tireTest);
  return (
    <>
      <ListItemRow>
        <div tw="flex items-center space-x-2 truncate">
          <PrimaryLinkButton tw="truncate">{tireTestName}</PrimaryLinkButton>
          <RegderBadge isREGDER={tireTest.isREGDER} regdersCount={tireTest.regders?.length} />
          <PriorityBadge priority={tireTest.priorityIndicator} />
          {tireTest.isLocked && (
            <LockClosedIcon tw="-mr-1 ml-2 h-3 w-3 text-gray-500 flex-shrink-0" />
          )}
        </div>
        <TireTestStatusBadge tireTest={tireTest} />
      </ListItemRow>
      <ListItemRow>
        <ListItemMeta>
          <TireTestMetaTags
            tireTest={tireTest}
            MetaTag={ListItemMetaTag}
            EmptyMetaTag={ListItemEmptyMetaTag}
          />
        </ListItemMeta>

        <ListItemMetaTag as="p">
          <CalendarOutlineIcon width={24} height={24} />
          <span>
            <TireTestDateLabel tireTest={tireTest} />
          </span>
        </ListItemMetaTag>
      </ListItemRow>
    </>
  );
};

/** @type {React.FC<{ tireTest: import("./TireTestQueries").TireTest }>} */
export const TireTestListItem = ({ tireTest }) => {
  return (
    <ListItem>
      <ListItemContent>
        <TireTestListItemSummary tireTest={tireTest} />
      </ListItemContent>
    </ListItem>
  );
};

export const TireTestAccordion = forwardRef((props, ref) => {
  return <Accordion ref={ref} as="ul" multiple collapsible {...props} />;
});

/** @type {React.FC<{ tireTest: import("./TireTestQueries").TireTest }>} */
export const TireTestAccordionPanelContent = ({ tireTest }) => {
  const { isExpanded } = useAccordionItemContext();
  const isForceAndMoment = tireTest.meta?.session?.isForceAndMoment ?? false;
  return isExpanded ? (
    <div tw="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 border-t p-4 px-16">
      {!isForceAndMoment && (
        <dl tw="space-y-1">
          <TireTestVMean tireTest={tireTest} reverse />
          <TireTestVMax tireTest={tireTest} reverse />
          <TireTestFzMean tireTest={tireTest} reverse />
          <TireTestFzMax tireTest={tireTest} reverse />
          <TireTestFyMean tireTest={tireTest} reverse />
          <TireTestFyMax tireTest={tireTest} reverse />
          <TireTestFxMean tireTest={tireTest} reverse />
          <TireTestFxMax tireTest={tireTest} reverse />
          <TireTestPeakZ tireTest={tireTest} reverse />
        </dl>
      )}
      <div tw="space-y-1">
        <TireTestFirstRunReference tireTest={tireTest} reverse />
        <TireTestSerialNumber tireTest={tireTest} reverse />
        <TireTestNothingToDeclare tireTest={tireTest} reverse />
        <TireTestDefects tireTest={tireTest} />
        <TireTestQuantity tireTest={tireTest} reverse />
        {!isForceAndMoment && <TireTestDeltaPressure tireTest={tireTest} reverse />}
        {tireTest.weightBeforeTest && <TireTestWeightBeforeTest tireTest={tireTest} reverse />}
        {tireTest.weightAfterTest && <TireTestWeightAfterTest tireTest={tireTest} reverse />}
        <TireTestCalulTest tireTest={tireTest} reverse />
      </div>
      {!isForceAndMoment && <TireTestDXPName tireTest={tireTest} reverse />}
    </div>
  ) : null;
};
/** @type {React.FC<{ tireTest: import("./TireTestQueries").TireTest }>} */
export const TireTestPostAnalysisAccordionPanelContent = ({ tireTest }) => {
  const { isExpanded } = useAccordionItemContext();
  let datCount = 0;
  return isExpanded ? (
    <>
      {tireTest && (
        <div tw="flex flex-col gap-4 border-t p-4">
          {tireTest.meta?.dxp?.file && (
            <div tw="flex gap-4 items-center">
              <p>DXP</p>
              <FileSelector
                key={`${tireTest._id}-${tireTest.meta.dxp.file.name}`}
                file={{
                  ...tireTest.meta?.dxp?.file,
                  name: tireTest.dxp,
                  deltaP: tireTest.lapCount ?? 6,
                  userMargins: {
                    FX: tireTest.fxUserMarginInPercent ?? 0,
                    FY: tireTest.fyUserMarginInPercent ?? 0,
                    FZ: tireTest.fzUserMarginInPercent ?? 0,
                  },
                  methodMargins: {
                    FX: tireTest.methodMargins?.fx ?? 0,
                    FY: tireTest.methodMargins?.fy ?? 0,
                    FZ: tireTest.methodMargins?.fz ?? 0,
                  },
                }}
              />
            </div>
          )}
          {(tireTest.stages || tireTest.regders) && (
            <div tw="flex flex-wrap items-center gap-4">
              <p>DAT</p>
              {tireTest.stages &&
                tireTest.stages.map((stage, stageIdx) => {
                  return (
                    <Fragment key={stageIdx}>
                      {stage.files &&
                        stage.files.map((file, fileIdx) => {
                          if (file.name.toLowerCase().endsWith(".dat")) {
                            datCount++;
                            return (
                              <FileSelector
                                key={`${tireTest._id}-${file.name}-stage-${stageIdx}-file-${fileIdx}`}
                                file={{ ...file, position: tireTest.positionOnVehicle }}
                              />
                            );
                          } else return null;
                        })}
                    </Fragment>
                  );
                })}
              {tireTest.regders &&
                tireTest.regders.map((regder, regderIdx) => {
                  return (
                    <Fragment key={regderIdx}>
                      {regder.files &&
                        regder.files.map((file, fileIdx) => {
                          if (file.name.toLowerCase().endsWith(".dat")) {
                            datCount++;
                            return (
                              <FileSelector
                                key={`${tireTest._id}-${file.name}-regder-${regderIdx}-file-${fileIdx}`}
                                file={{ ...file, position: tireTest.positionOnVehicle }}
                              />
                            );
                          } else return null;
                        })}
                    </Fragment>
                  );
                })}
              {datCount === 0 && <p tw="opacity-70 text-sm">No DAT files found for this test</p>}
            </div>
          )}
        </div>
      )}
    </>
  ) : null;
};
